import { useMergeRefs } from "@floating-ui/react";
import { mergeProps } from "@react-aria/utils";
import cx from "classnames";
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import { useFocus, useKeyboard, usePress } from "react-aria";

import BadgeNext from "ds/components/BadgeNext";
import Box from "ds/components/Box";
import TextEllipsis from "ds/components/TextEllipsis";
import Typography from "ds/components/Typography";
import { TreeItem, TreeItemContent, TreeItemToggle } from "ds/components/Tree";
import Toggle from "ds/components/Toggle";

import { TreeListHighlightLabel } from "../HighlightLabel";
import { TreeListItemWithCheckboxProps } from "../types";
import styles from "./styles.module.css";

const TreeListItemWithCheckbox = <T extends object>({
  id,
  name,
  item,
  isExpanded,
  isDisabled,
  searchQuery,
  postfix,
  selected,
  innerRef,
  hasFocus,
  isChecked,
  onToggle,
  onFocus,
  onBlur,
  onKeyDown,
  onClick,
  onCheckboxChange,
  isOrigin,
}: TreeListItemWithCheckboxProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);
  const mergedRef = useMergeRefs([innerRef, ref]);
  const [isFocusHidden, setIsFocusHidden] = useState(false);

  const inheritEntities = (item as { inheritEntities?: boolean }).inheritEntities;
  const inheritancePostfix =
    inheritEntities !== undefined ? (
      <Typography variant="p-body3" tag="span" className={styles.inheritanceInfo}>
        {inheritEntities ? "Yes" : "No"}
      </Typography>
    ) : null;

  const finalPostfix = inheritancePostfix || postfix;

  const handleToggleIconPressEnd = useCallback(() => {
    setIsFocusHidden(true);
    onFocus?.();
  }, [onFocus]);

  const handleBlur = useCallback(() => {
    setIsFocusHidden(false);
    onBlur?.();
  }, [onBlur]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => onKeyDown?.(e, id), [id, onKeyDown]);

  const handleKeyup = useCallback(() => {
    setIsFocusHidden(false);
  }, []);

  const handleCheckboxClick = useCallback(() => {
    if (!isDisabled && onCheckboxChange) {
      onCheckboxChange(!isChecked);
    }
  }, [isChecked, isDisabled, onCheckboxChange]);

  const { focusProps } = useFocus({
    onBlur: handleBlur,
  });

  const { pressProps: wrapperPressProps } = usePress({
    onPress: onClick,
    isDisabled,
  });

  const { keyboardProps } = useKeyboard({
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyup,
  });

  useEffect(() => {
    const elementRef = ref.current;
    if (hasFocus && elementRef) {
      elementRef.focus();

      if (!isFocusHidden) {
        elementRef.scrollIntoView({ block: "center", inline: "nearest" });
      }
    }
  }, [hasFocus, isFocusHidden]);

  return (
    <TreeItem
      {...mergeProps(focusProps, keyboardProps, wrapperPressProps)}
      isSelected={selected}
      isDisabled={isDisabled}
      isFocusHidden={isFocusHidden}
      isInteractive={!!onClick}
      isIndented={!onToggle}
      ref={mergedRef}
      tabIndex={-1}
      role="treeitem"
      aria-selected={selected}
      aria-disabled={isDisabled}
      aria-expanded={isExpanded}
      className={styles.treeListItem}
    >
      {!!onToggle && (
        <TreeItemToggle
          isExpanded={isExpanded}
          onPress={onToggle}
          onPressEnd={handleToggleIconPressEnd}
          className={styles.toggle}
        />
      )}

      <TreeItemContent>
        <Toggle
          variant="checkbox"
          onChange={handleCheckboxClick}
          isSelected={isChecked}
          isDisabled={isDisabled}
          className={styles.checkboxItem}
        >
          <TextEllipsis
            tooltip={name}
            tooltipPlacement="top-end"
            tooltipWidthMode="maxWidthXl"
            delay={400}
          >
            {(ellipsisProps) => (
              <Typography
                {...ellipsisProps}
                variant="p-body3"
                tag="span"
                className={cx(
                  ellipsisProps.className,
                  cx(styles.name, isDisabled && styles.nameDisabled)
                )}
              >
                {isDisabled ? name : <TreeListHighlightLabel query={searchQuery} label={name} />}
              </Typography>
            )}
          </TextEllipsis>
          {isOrigin && <BadgeNext variant="blue" type="weak" text="Origin" />}
          {finalPostfix && (
            <Box className={cx(styles.postfix, isDisabled && styles.postfixDisabled)} shrink="0">
              {finalPostfix}
            </Box>
          )}
        </Toggle>
      </TreeItemContent>
    </TreeItem>
  );
};

TreeListItemWithCheckbox.displayName = "DS.TreeList.ItemWithCheckbox";

export default TreeListItemWithCheckbox;
